import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Autorenew as RotateIcon,
    PowerSettingsNew as PowerIcon,
    ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { AdminPageContainer, FormField } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import {
    getServiceAccounts,
    createServiceAccount,
    rotateServiceAccount,
    activateServiceAccount,
    deactivateServiceAccount,
    deleteServiceAccount,
} from '../../redux/ducks/serviceAccount';
import { showAlert } from '../../redux/slices/alerts';
import { formatDateTime } from '../../utils/dateUtils';

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const ClientIdCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const MonospaceCaption = styled(Typography)({
    fontFamily: 'monospace',
});

const HelperBlurb = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const TopFormField = styled(FormField)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const NoticeAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const MonospaceField = styled(TextField)({
    '& .MuiInputBase-root': {
        fontFamily: 'monospace',
    },
});

const AdminServiceAccounts = () => {
    const dispatch = useDispatch();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [label, setLabel] = useState('');
    const [credentials, setCredentials] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmRotate, setConfirmRotate] = useState(null);

    useEffect(() => {
        loadAccounts();
    }, []);

    const loadAccounts = () => {
        setLoading(true);
        dispatch(getServiceAccounts((data) => {
            setAccounts(Array.isArray(data) ? data : []);
            setLoading(false);
        }));
    };

    const handleCreate = () => {
        dispatch(createServiceAccount({ label: label.trim() }, (data) => {
            if (data?.client_id && data?.client_secret) {
                setCredentials(data);
                setCreateOpen(false);
                setLabel('');
                loadAccounts();
            }
        }));
    };

    const handleRotate = (account) => {
        dispatch(rotateServiceAccount(account._id, (data) => {
            if (data?.client_id && data?.client_secret) {
                setCredentials({ ...data, rotated: true });
                setConfirmRotate(null);
            }
        }));
    };

    const handleToggleActive = (account) => {
        const action = account.active ? deactivateServiceAccount : activateServiceAccount;
        dispatch(action(account._id, (data) => {
            if (!data) return;
            loadAccounts();
        }));
    };

    const handleDelete = (account) => {
        dispatch(deleteServiceAccount(account._id, (data) => {
            if (!data) return;
            setConfirmDelete(null);
            loadAccounts();
        }));
    };

    const copyToClipboard = (value, what) => {
        navigator.clipboard?.writeText(value);
        dispatch(showAlert({ severity: 'success', message: `${what} copied to clipboard` }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Service Accounts"
                subtitle="Machine-to-machine credentials for API agents. Secrets are shown once and cannot be recovered."
                actions={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                    >
                        Create Service Account
                    </Button>
                }
            />

            {loading ? (
                <LoadingBox>
                    <CircularProgress />
                </LoadingBox>
            ) : accounts.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        No service accounts yet. Click "Create Service Account" to provision one.
                    </Typography>
                </EmptyState>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Label</strong></TableCell>
                                <TableCell><strong>Client ID</strong></TableCell>
                                <TableCell><strong>Role</strong></TableCell>
                                <TableCell align="center"><strong>Status</strong></TableCell>
                                <TableCell><strong>Created</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accounts.map((account) => (
                                <TableRow key={account._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {account.first_name} {account.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {account.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <ClientIdCell>
                                            <MonospaceCaption variant="caption">
                                                {account._id}
                                            </MonospaceCaption>
                                            <Tooltip title="Copy client_id">
                                                <IconButton size="small" onClick={() => copyToClipboard(account._id, 'client_id')}>
                                                    <CopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ClientIdCell>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {account.role?.name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={account.active ? 'Active' : 'Inactive'}
                                            size="small"
                                            color={account.active ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {formatDateTime(account.createdAt) || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Rotate secret">
                                            <IconButton size="small" color="primary" onClick={() => setConfirmRotate(account)}>
                                                <RotateIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={account.active ? 'Deactivate' : 'Activate'}>
                                            <IconButton size="small" color={account.active ? 'warning' : 'success'} onClick={() => handleToggleActive(account)}>
                                                <PowerIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(account)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Service Account</DialogTitle>
                <DialogContent>
                    <HelperBlurb variant="body2" color="text.secondary">
                        A new internal service account will be created with the lowest-level Internal role.
                        The client_secret will be displayed once after creation.
                    </HelperBlurb>
                    <TopFormField>
                        <TextField
                            fullWidth
                            label="Label (optional)"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            helperText="A friendly name to identify this agent (e.g., 'Quote Bot')"
                        />
                    </TopFormField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleCreate}>Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!credentials} onClose={() => setCredentials(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{credentials?.rotated ? 'Secret Rotated' : 'Service Account Created'}</DialogTitle>
                <DialogContent>
                    <NoticeAlert severity="warning">
                        Copy these credentials now. The secret cannot be recovered later.
                    </NoticeAlert>
                    <FormField>
                        <MonospaceField
                            fullWidth
                            label="client_id"
                            value={credentials?.client_id || ''}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton size="small" onClick={() => copyToClipboard(credentials.client_id, 'client_id')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </FormField>
                    <FormField>
                        <MonospaceField
                            fullWidth
                            label="client_secret"
                            value={credentials?.client_secret || ''}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton size="small" onClick={() => copyToClipboard(credentials.client_secret, 'client_secret')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </FormField>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setCredentials(null)}>I've stored these</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={() => handleDelete(confirmDelete)}
                message={`Delete service account "${confirmDelete?.first_name} ${confirmDelete?.last_name}"? This cannot be undone and any agent using its credentials will stop working.`}
            />

            <ConfirmDialog
                open={!!confirmRotate}
                onClose={() => setConfirmRotate(null)}
                onConfirm={() => handleRotate(confirmRotate)}
                message={`Rotate the secret for "${confirmRotate?.first_name} ${confirmRotate?.last_name}"? The previous secret will stop working immediately.`}
            />
        </AdminPageContainer>
    );
};

export default AdminServiceAccounts;
